import { useState } from "react"
import { useRouter } from "next/router"

import { useClerk, useOrganization } from "@clerk/nextjs"
import type { OrganizationMembershipResource } from "@clerk/nextjs/types"
import { Avatar } from "@coval/ui/avatar"
import { Badge } from "@coval/ui/badge"
import { Button } from "@coval/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@coval/ui/dialog"
import { useCopyToClipboardFeedback } from "@coval/ui/use-copy-to-clipboard-feedback"
import { Copy, LockKeyhole, Mail } from "lucide-react"

import { logger } from "@/lib/logger"
import { getRouteOrganizationSlug } from "@/utils/workspaceRoutes"

const PAGE_SIZE = 20
const log = logger("OrganizationAdminContacts")

export function OrganizationPermissionNotice({
  reason,
  request,
  disabled = false,
}: {
  reason: string
  request: string
  disabled?: boolean
}) {
  const { organization, isLoaded } = useOrganization()
  const clerk = useClerk()
  const router = useRouter()
  const slug = getRouteOrganizationSlug(router.asPath)
  const sessionId = clerk.session?.id
  const ready = !!(
    isLoaded &&
    organization?.id &&
    slug &&
    organization?.slug === slug &&
    clerk.user?.id &&
    sessionId
  )
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-coval-border bg-surface-1 p-4">
      <LockKeyhole
        className="size-4 shrink-0 text-coval-text-muted"
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1 text-sm text-coval-text-muted">
        <p>{reason}</p>
        {!ready && (
          <p className="mt-1 text-xs">
            Open an organization’s settings to find its admins.
          </p>
        )}
      </div>
      {ready && organization ? (
        <AdminContactButton
          key={`${organization.id}:${slug}:${sessionId}`}
          organizationId={organization.id}
          organizationName={organization.name}
          request={request}
          disabled={disabled}
        />
      ) : (
        <Button variant="secondary" disabled>
          Ask an admin
        </Button>
      )}
    </div>
  )
}

function AdminContactButton({
  organizationId,
  organizationName,
  request,
  disabled,
}: {
  organizationId: string
  organizationName: string
  request: string
  disabled: boolean
}) {
  const [open, setOpen] = useState(false)
  const { user } = useClerk()
  const { memberships } = useOrganization({
    memberships: open
      ? { role: ["org:admin"], pageSize: PAGE_SIZE, keepPreviousData: true }
      : undefined,
  })
  const data = memberships?.data
  const scopeError = data?.some(
    (member) =>
      member.organization.id !== organizationId || member.role !== "org:admin",
  )
  const error = memberships?.error || scopeError
  const retry = async () => {
    try {
      await memberships?.revalidate?.()
    } catch {
      log.warn("Could not reload administrator contacts")
    }
  }
  return (
    <>
      <Button
        variant="secondary"
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        Ask an admin
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>Ask an organization admin</DialogTitle>
            <DialogDescription>
              Ask an admin of {organizationName} about {request}. Email opens a
              draft in your email app; nothing is sent automatically.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            {error ? (
              <div role="alert" className="space-y-3">
                <p className="text-sm text-coval-text-muted">
                  Could not load administrator contacts. Ask a known
                  organization admin or retry.
                </p>
                <Button
                  variant="secondary"
                  onClick={() => void retry()}
                  loading={!!memberships?.isFetching}
                >
                  Retry
                </Button>
              </div>
            ) : !data ? (
              <p role="status" className="text-sm text-coval-text-muted">
                Loading admins…
              </p>
            ) : data.length === 0 ? (
              <p className="text-sm text-coval-text-muted">
                No administrator contacts are available. Ask your organization
                owner for help.
              </p>
            ) : (
              <div className="divide-y divide-coval-border">
                {[...data]
                  .sort((left, right) =>
                    (
                      left.publicUserData?.firstName ||
                      left.publicUserData?.identifier ||
                      ""
                    ).localeCompare(
                      right.publicUserData?.firstName ||
                        right.publicUserData?.identifier ||
                        "",
                    ),
                  )
                  .map((member) => (
                    <AdminContactRow
                      key={member.id}
                      member={member}
                      currentUserId={user?.id}
                      organizationName={organizationName}
                      request={request}
                    />
                  ))}
              </div>
            )}
            {!error && data && (
              <div className="flex items-center justify-between gap-3 text-sm text-coval-text-muted">
                <span>
                  Page {memberships?.page ?? 1} ·{" "}
                  {memberships?.count ?? data.length} admins
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    disabled={
                      memberships?.isFetching || !memberships?.hasPreviousPage
                    }
                    onClick={() => memberships?.fetchPrevious?.()}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={
                      memberships?.isFetching || !memberships?.hasNextPage
                    }
                    onClick={() => memberships?.fetchNext?.()}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function AdminContactRow({
  member,
  currentUserId,
  organizationName,
  request,
}: {
  member: OrganizationMembershipResource
  currentUserId?: string
  organizationName: string
  request: string
}) {
  const { copied, copy } = useCopyToClipboardFeedback()
  const person = member.publicUserData
  const name =
    [person?.firstName, person?.lastName].filter(Boolean).join(" ") ||
    person?.identifier ||
    "Organization admin"
  const email =
    person?.identifier && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(person.identifier)
      ? person.identifier
      : null
  const subject = `Settings access for ${organizationName}`
  const body = `Hi,\n\nI’d like to ask about ${request} in ${organizationName}. Could you review my access or the relevant organization policy?\n\nThanks!`
  return (
    <div className="flex flex-wrap items-center gap-3 py-3">
      <Avatar name={name} src={person?.imageUrl} decorative />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-sm font-medium">
          {name}
          {person?.userId === currentUserId && (
            <Badge variant="outline">You</Badge>
          )}
        </p>
        <p className="break-all text-xs text-coval-text-muted">
          {email || "Email unavailable"}
        </p>
      </div>
      {email && (
        <>
          <Button asChild variant="secondary">
            <a
              href={`mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`}
            >
              <Mail />
              Email
            </a>
          </Button>
          <Button
            variant="ghost"
            icon
            aria-label={`Copy email for ${name}`}
            tooltip={copied ? "Copied!" : "Copy email"}
            tooltipProps={{ forceOpen: copied }}
            onClick={() => void copy(email)}
          >
            <Copy />
          </Button>
        </>
      )}
    </div>
  )
}
